import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { AdminNav } from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";

const Field = ({ id, label, value, onChange, textarea = false, placeholder = "", testid }) => (
  <div>
    <label htmlFor={id} className="text-xs tracking-[0.25em] uppercase text-soft">{label}</label>
    {textarea ? (
      <textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`${inputCls} resize-y`} data-testid={testid} />
    ) : (
      <input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={inputCls} data-testid={testid} />
    )}
  </div>
);

export default function AdminBooklet() {
  useSeo({ title: "Booklet details", noindex: true });
  const [token, setToken] = useState(localStorage.getItem("wd-admin") || "");
  const [form, setForm] = useState({ title_en: "", title_nl: "", desc_en: "", desc_nl: "", price_eur: "", cover_url: "", available: false });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/products/booklet`);
      setForm({
        title_en: data.title_en || "", title_nl: data.title_nl || "",
        desc_en: data.desc_en || "", desc_nl: data.desc_nl || "",
        price_eur: data.price_eur ?? "", cover_url: data.cover_url || "",
        available: !!data.available,
      });
    } catch { /* keep empty form */ }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setResult(null);
    try {
      await axios.post(`${API}/products/booklet`, {
        title_en: form.title_en || null, title_nl: form.title_nl || null,
        desc_en: form.desc_en || null, desc_nl: form.desc_nl || null,
        price_eur: form.price_eur === "" ? null : Number(form.price_eur),
        cover_url: form.cover_url || null,
        available: form.available,
      }, { headers: { "X-Admin-Token": token } });
      setResult("Saved — the booklet section on the First Periods page now uses these details.");
    } catch (err) {
      setResult(err?.response?.status === 403 ? "That key isn't right — check it and try again." : "Something went wrong — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="bg-cream min-h-screen" data-testid="admin-booklet-page">
      <section className="mx-auto max-w-2xl px-6 pt-40 pb-24">
        <div className="flex items-center gap-3 text-clay">
          <BookOpen size={20} strokeWidth={1.25} />
          <p className="text-xs tracking-[0.35em] uppercase">First Period Booklet</p>
        </div>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Booklet details</h1>
        <p className="mt-4 text-base leading-relaxed text-soft">
          Everything you save here appears on the First Periods page. While availability is off, the booklet simply shows as
          "Coming soon" — no price and no order button. When the booklet is ready, fill in the details, switch availability on,
          and the price and order button appear. Online payment activates separately — just ask when fulfilment details are final.
        </p>

        <div className="mt-8">
          <AdminNav current="/admin/booklet" />
        </div>

        <form onSubmit={save} className="mt-12 space-y-8" data-testid="booklet-form">
          <div>
            <label htmlFor="bk-token" className="text-xs tracking-[0.25em] uppercase text-soft">Your key</label>
            <input id="bk-token" type="password" value={token}
              onChange={(e) => { setToken(e.target.value); localStorage.setItem("wd-admin", e.target.value); }}
              placeholder="Same key as the letter page" className={inputCls} data-testid="booklet-token-input" />
          </div>
          <Field id="bk-title-en" label="Title — English" value={form.title_en} onChange={set("title_en")} placeholder="The First Period Booklet" testid="booklet-title-en" />
          <Field id="bk-title-nl" label="Titel — Nederlands" value={form.title_nl} onChange={set("title_nl")} placeholder="Het Eerste Menstruatie-boekje" testid="booklet-title-nl" />
          <Field id="bk-desc-en" label="Short description — English" value={form.desc_en} onChange={set("desc_en")} textarea testid="booklet-desc-en" />
          <Field id="bk-desc-nl" label="Korte beschrijving — Nederlands" value={form.desc_nl} onChange={set("desc_nl")} textarea testid="booklet-desc-nl" />
          <div className="grid gap-8 sm:grid-cols-2">
            <Field id="bk-price" label="Price in € (e.g. 12.50)" value={form.price_eur} onChange={set("price_eur")} testid="booklet-price" />
            <Field id="bk-cover" label="Cover image URL or path" value={form.cover_url} onChange={set("cover_url")} placeholder="/images/booklet.jpg" testid="booklet-cover" />
          </div>
          <label className="flex items-center gap-3 text-sm text-ink cursor-pointer" data-testid="booklet-available-label">
            <input type="checkbox" checked={form.available} onChange={(e) => set("available")(e.target.checked)}
              className="h-4 w-4 accent-[#9C5E4C]" data-testid="booklet-available" />
            The booklet is available — show the price and order button on the page
          </label>
          <button type="submit" disabled={saving} data-testid="booklet-save-button"
            className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-50">
            {saving ? "Saving…" : "Save booklet details"}
            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>

        {result && (
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-8 border border-clay/30 bg-parchment p-6 font-serif italic text-lg text-ink"
            data-testid="booklet-result">
            {result}
          </motion.p>
        )}
        <p className="mt-8 text-xs leading-relaxed text-soft/70">
          Cover image: paste any image URL, or send the cover file in the chat and it will be placed at /images/booklet.jpg for you.
        </p>
      </section>
    </main>
  );
}
